import type { CSSProperties, FC, ReactNode } from 'react'
import type { MotionValue } from 'motion/react'

import { useRef } from 'react'
import { motion, useScroll, useTransform } from 'motion/react'
import { ArrowRight, Phone, Star } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useReduceMotion } from '@/hooks/use-reduce-motion'
import { arrowClass, buttonClass } from '@/components/generals/button'
import Magnetic from '@/components/generals/magnetic'
import QuoteLink from '@/components/generals/quote-link'
import Portrait, { TiltCard } from '@/components/landing/portrait'
import StarBurst from '@/components/landing/star-burst'
import { contact, hero, quoteLabel } from '@/constants/content'

interface LineProps {
    children: ReactNode
    delay: number
    x: MotionValue<string>
    still: boolean
    className?: string
}

// One headline line: it rises out of its own mask on load, then drifts sideways as the hero scrolls away.
const Line: FC<LineProps> = ({ children, delay, x, still, className }): ReactNode => {
    return (
        <motion.span style={still ? undefined : { x }} className='block'>
            <span className='-mb-[0.14em] block overflow-hidden pb-[0.14em]'>
                <span
                    className={cn('relative inline-block origin-bottom-left animate-rise', className)}
                    style={{ animationDelay: `${delay}ms` } as CSSProperties}
                >
                    {children}
                </span>
            </span>
        </motion.span>
    )
}

const Hero: FC = (): ReactNode => {
    const ref = useRef<HTMLElement>(null)
    const reduce = useReduceMotion()
    const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] })

    const lineOne = useTransform(scrollYProgress, [0, 1], ['0%', '-16%'])
    const lineTwo = useTransform(scrollYProgress, [0, 1], ['0%', '12%'])
    const lineThree = useTransform(scrollYProgress, [0, 1], ['0%', '-7%'])
    const cardY = useTransform(scrollYProgress, [0, 1], [0, -140])
    const cardRotate = useTransform(scrollYProgress, [0, 1], [-4, 5])
    const badgeRotate = useTransform(scrollYProgress, [0, 1], [0, 160])

    return (
        <section
            ref={ref}
            id='top'
            className='relative overflow-x-clip bg-paper pb-20 pt-24 sm:pt-32 lg:flex lg:min-h-[100svh] lg:items-center lg:pb-24'
        >
            <div
                aria-hidden='true'
                className='pointer-events-none absolute inset-0 opacity-[0.08] [background-image:radial-gradient(var(--color-ink)_1.2px,transparent_1.2px)] [background-size:22px_22px] [mask-image:linear-gradient(to_bottom,black_30%,transparent)]'
            />

            <div className='shell relative grid gap-16 lg:grid-cols-12 lg:items-center lg:gap-8'>
                <div className='lg:col-span-8'>
                    <p className='eyebrow flex items-center gap-3 animate-slide-in'>
                        <span aria-hidden='true' className='relative flex size-2.5'>
                            <span className='absolute inset-0 animate-ping rounded-full bg-blaze opacity-70' />
                            <span className='relative size-2.5 rounded-full bg-blaze' />
                        </span>

                        {hero.eyebrow}
                    </p>

                    <h1 className='mt-5 font-heading text-[14.5vw] font-bold leading-[0.9] tracking-[-0.015em] wdth-78 sm:mt-6 sm:text-[11vw] lg:mt-8 lg:text-[9.2vw] 2xl:text-[8.75rem]'>
                        <Line delay={80} x={lineOne} still={reduce}>
                            {hero.headline.lead}
                        </Line>

                        {' '}

                        <Line delay={190} x={lineTwo} still={reduce}>
                            {hero.headline.punch}

                            <svg
                                aria-hidden='true'
                                viewBox='0 0 300 14'
                                preserveAspectRatio='none'
                                className='pointer-events-none absolute left-[0.02em] top-[0.83em] h-[0.15em] w-[calc(100%_-_0.32em)] overflow-visible'
                            >
                                <path
                                    d='M3 10 C 70 3, 160 1, 297 7'
                                    pathLength={1}
                                    fill='none'
                                    stroke='#22FFB2'
                                    strokeWidth={5}
                                    strokeLinecap='round'
                                    strokeDasharray={1}
                                    className='animate-draw [animation-delay:1050ms]'
                                />
                            </svg>
                        </Line>

                        {' '}

                        <Line delay={300} x={lineThree} still={reduce} className='text-blaze-deep'>
                            {hero.headline.tail}
                        </Line>
                    </h1>

                    <div className='mt-7 max-w-[36rem] sm:mt-10 lg:mt-12'>
                        <p className='text-lg leading-relaxed text-ink/75 animate-slide-in [animation-delay:520ms] sm:text-xl'>
                            {hero.subhead}
                        </p>

                        <div className='mt-7 flex flex-col gap-3 animate-slide-in sm:mt-8 [animation-delay:640ms] sm:flex-row sm:flex-wrap'>
                            <Magnetic className='w-full sm:w-auto'>
                                <QuoteLink className={buttonClass({ size: 'lg', className: 'w-full bg-[#22FFB2]' })}>
                                    {quoteLabel}
                                    <ArrowRight aria-hidden='true' className={arrowClass} />
                                </QuoteLink>
                            </Magnetic>

                            <Magnetic className='w-full sm:w-auto'>
                                <a href={contact.phoneHref} className={buttonClass({ variant: 'paper', size: 'lg', className: 'w-full' })}>
                                    <Phone aria-hidden='true' className='size-5 transition-transform duration-300 ease-bounce group-hover/button:-rotate-12' />
                                    {hero.call}
                                </a>
                            </Magnetic>
                        </div>

                        <p className='mt-5 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[0.9375rem] sm:mt-6 sm:justify-start font-medium animate-slide-in [animation-delay:760ms] sm:text-base'>
                            <span aria-hidden='true' className='flex gap-0.5'>
                                {[0, 1, 2, 3, 4].map(index => (
                                    <Star
                                        key={index}
                                        className='size-4 animate-pop fill-blaze text-ink'
                                        style={{ animationDelay: `${900 + index * 90}ms` }}
                                    />
                                ))}
                            </span>

                            <span className='font-bold'>
                                {hero.rating} on{' '}
                                <a
                                    href={hero.upworkUrl}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className='group relative inline-block'
                                >
                                    Upwork
                                    <span
                                        aria-hidden='true'
                                        className='absolute -bottom-0.5 left-0 h-[2px] w-full origin-right scale-x-0 rounded-full bg-blaze transition-transform duration-300 ease-snap group-hover:origin-left group-hover:scale-x-100'
                                    />
                                </a>
                            </span>

                            <span aria-hidden='true' className='text-ink/40'>·</span>

                            <span className='text-ink/75'>
                                {hero.trust}
                            </span>
                        </p>
                    </div>
                </div>

                <div className='lg:col-span-4'>
                    <motion.div
                        style={reduce ? undefined : { y: cardY, rotate: cardRotate }}
                        className='relative mx-auto w-[min(100%,21rem)] lg:w-full lg:max-w-[26rem]'
                    >
                        <div className='animate-deal [animation-delay:250ms]'>
                            <TiltCard>
                                <Portrait tone='blaze' />
                            </TiltCard>
                        </div>

                        <div className='absolute -left-6 -top-8 animate-pop [animation-delay:1150ms] sm:-left-10 sm:-top-10'>
                            <StarBurst rotate={reduce ? undefined : badgeRotate} />
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    )
}

export default Hero
